import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { FluentProvider } from '@fluentui/react-components';

import { transformrLightTheme } from './theme/transformrTheme';
import { Layout } from './components/layout/Layout';
import { RootGate } from './auth/RootGate';
import { AuthCallback } from './components/AuthCallback';
import { TeamsContextProvider } from './components/TeamsContext';
import { HomePage } from './pages/HomePage';
import { LoginPage } from './pages/LoginPage';
import { ReportsIndex } from './pages/reports/ReportsIndex';
import { FeatureListPage } from './pages/reports/FeatureListPage';
import { FeatureProgressPage } from './pages/reports/FeatureProgressPage';
import { EpicListPage } from './pages/reports/EpicListPage';
import { EpicTreePage } from './pages/reports/EpicTreePage';
import { EpicProgressPage } from './pages/reports/EpicProgressPage';
import { VelocityPage } from './pages/reports/VelocityPage';
import { ProgrammeIndex } from './pages/reports/ProgrammeIndex';
import { WsjfBacklogPage } from './pages/reports/programme/WsjfBacklogPage';
import { ValueAtRiskPage } from './pages/reports/programme/ValueAtRiskPage';
import { BugAgingPage } from './pages/reports/programme/BugAgingPage';
import { BurnupPage } from './pages/reports/programme/BurnupPage';
import { CostVsEstimatePage } from './pages/reports/programme/CostVsEstimatePage';
import { ActivityHeatmapPage } from './pages/reports/programme/ActivityHeatmapPage';
import { CfdPage } from './pages/reports/programme/CfdPage';
import { SpecQualityPage } from './pages/reports/programme/SpecQualityPage';
import { CycleTimePage } from './pages/reports/programme/CycleTimePage';
import { ConcurrentTimelinePage } from './pages/reports/programme/ConcurrentTimelinePage';
import { CustomerReportsIndex } from './pages/reports/CustomerReportsIndex';
import { CustomerRoadmapPage } from './pages/reports/customer/CustomerRoadmapPage';
import { CustomerProgressPage } from './pages/reports/customer/CustomerProgressPage';
import { CustomerUpcomingPage } from './pages/reports/customer/CustomerUpcomingPage';
import { CustomerDecisionsRisksPage } from './pages/reports/customer/CustomerDecisionsRisksPage';
import { SignoffQueuePage } from './pages/reports/customer/SignoffQueuePage';
// DOR-F01: admin moved to Operations Manager (OPSM-F31). DOR is reporting-only —
// admin pages, routes and nav removed.

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 },
  },
});

function App() {
  return (
    <FluentProvider theme={transformrLightTheme}>
      <TeamsContextProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            {/* /welcome renders outside the Layout shell — it's a chromeless two-column
                landing page that hands off to /.auth/login/aad for the real OAuth flow.
                /login is unused locally (the SWA CLI emulator intercepts it for direct
                Microsoft sign-in); production also accepts it via SPA fallback. */}
            <Route path="/welcome" element={<LoginPage />} />
            <Route path="/login" element={<LoginPage />} />
            {/* MSAL redirect landing (redirectUri). Processes the auth response, then
                returns the user to their destination. */}
            <Route path="/auth/callback" element={<AuthCallback />} />
            {/* Legacy SWA callback path — harmless redirect in case anything still points here. */}
            <Route path="/.auth/*" element={<Navigate to="/welcome" replace />} />
            {/* Everything below requires sign-in. RootGate (src/auth) bounces unauthenticated
                visitors to /welcome, shows a spinner while access resolves, a retry screen on
                transient/infra errors, and the no-access screen only for a genuinely
                un-provisioned account (SPA-Auth Standard). */}
            <Route element={<RootGate />}>
            <Route element={<Layout />}>
              <Route path="/" element={<HomePage />} />
              {/* Per-project reports (existing) */}
              <Route path="/reports" element={<ReportsIndex />} />
              <Route path="/reports/features" element={<FeatureListPage />} />
              <Route path="/reports/projects/:projectId/epics/:epicId/features/:featureId/progress" element={<FeatureProgressPage />} />
              <Route path="/reports/projects/:projectId/epics" element={<EpicListPage />} />
              <Route path="/reports/projects/:projectId/epics/:epicId/progress" element={<EpicProgressPage />} />
              <Route path="/reports/projects/:projectId/epics/:epicId/tree" element={<EpicTreePage />} />
              <Route path="/reports/projects/:projectId/velocity" element={<VelocityPage />} />
              {/* Programme dashboards — internal only */}
              <Route path="/reports/programme" element={<ProgrammeIndex />} />
              <Route path="/reports/programme/burnup" element={<BurnupPage />} />
              <Route path="/reports/programme/wsjf" element={<WsjfBacklogPage />} />
              <Route path="/reports/programme/value-at-risk" element={<ValueAtRiskPage />} />
              <Route path="/reports/programme/bug-aging" element={<BugAgingPage />} />
              <Route path="/reports/programme/cost-vs-estimate" element={<CostVsEstimatePage />} />
              <Route path="/reports/programme/activity" element={<ActivityHeatmapPage />} />
              <Route path="/reports/programme/cfd" element={<CfdPage />} />
              <Route path="/reports/programme/spec-quality" element={<SpecQualityPage />} />
              <Route path="/reports/programme/cycle-time" element={<CycleTimePage />} />
              <Route path="/reports/programme/concurrent-projects" element={<ConcurrentTimelinePage />} />
              {/* Customer-facing reports */}
              <Route path="/reports/customer" element={<CustomerReportsIndex />} />
              <Route path="/reports/customer/roadmap" element={<CustomerRoadmapPage />} />
              <Route path="/reports/customer/progress" element={<CustomerProgressPage />} />
              <Route path="/reports/customer/upcoming" element={<CustomerUpcomingPage />} />
              <Route path="/reports/customer/decisions-risks" element={<CustomerDecisionsRisksPage />} />
              <Route path="/reports/customer/signoff-queue" element={<SignoffQueuePage />} />
              {/* Admin moved to Operations Manager (DOR-F01 / OPSM-F31) — /admin/* now 404s to "/". */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
            </Route>
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
      </TeamsContextProvider>
    </FluentProvider>
  );
}

export default App;
